import { useCallback } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useCreateCustomer } from '@/hooks/useCustomerType';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { useUpdateSubscription } from '@/hooks/useSubscription';
import { SubmitConfig } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import {
  generateSecurePassword,
  generateUsername,
} from '@/sections/customerManagement/components/modals/AddCustomerModal/utils';
import { createSubscriptionIfNeeded } from '@/sections/customerManagement/utils';
import { parseBugBountyUrl } from '@/sections/customerManagement/utils/bugBountyUrl';
import { useAuth } from '@/state/auth';

/**
 * Hook for handling customer creation submission
 */
export const useCustomerSubmit = ({
  formData,
  enabledFlags,
  onSuccess,
  onClose,
}: SubmitConfig) => {
  const createCustomer = useCreateCustomer();
  const { mutateAsync: updateSubscription } = useUpdateSubscription();
  const axios = useAxios();
  const { getToken } = useAuth();
  const enableRbac = useRbacEnabled();

  const handleSubmit = useCallback(async () => {
    // Generate credentials
    const username = generateUsername(formData.settings_display_name);
    const password = generateSecurePassword();

    // Create the customer
    await createCustomer.mutateAsync({
      username,
      password,
      settings_display_name: formData.settings_display_name,
      customer_type: formData.customer_type || undefined,
      profile_picture: formData.profile_picture,
      enabled_flags: enabledFlags,
      plextrac_id: formData.plextrac_id || undefined,
      collaborators:
        formData.collaborators.length > 0
          ? formData.collaborators.map(item => ({
              email: item.collaborator,
              role: enableRbac ? item.role : 'admin',
            }))
          : [],
      parent_account: formData.parent_account || undefined,
      allowed_domains:
        formData.allowed_domains.length > 0
          ? formData.allowed_domains
          : undefined,
    });

    // Create subscription if applicable
    const token = await getToken();
    await createSubscriptionIfNeeded({
      formData,
      username,
      updateSubscription,
    });

    // Auto-configure bug bounty platform integration
    if (
      formData.customer_type === 'BUG_BOUNTY' &&
      formData.hackerone_program_url
    ) {
      const parsed = parseBugBountyUrl(formData.hackerone_program_url);

      if (parsed) {
        try {
          await axios.post(
            `/account/${parsed.platform}`,
            {
              username: parsed.platform,
              value: parsed.handle,
              secret: {
                username: parsed.platform,
                value: parsed.handle,
              },
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Account: username,
              },
            }
          );
        } catch (err) {
          console.warn('Failed to auto-configure bug bounty integration:', err);
          toast.warning(
            'Customer created, but bug bounty platform integration could not be configured. You can set it up manually in customer settings.'
          );
        }

        // Auto-sync HackerOne program scope to import seeds
        try {
          await axios.post('/hackerone/sync-scope', null, {
            headers: {
              Authorization: `Bearer ${token}`,
              Account: username,
            },
          });
        } catch (err) {
          console.warn('Failed to auto-sync HackerOne scope:', err);
        }
      }
    }

    // Trigger success callback with created customer email and close modal
    await onSuccess?.(username);
    onClose();
  }, [
    formData,
    enabledFlags,
    enableRbac,
    createCustomer,
    updateSubscription,
    axios,
    getToken,
    onSuccess,
    onClose,
  ]);

  return {
    handleSubmit,
    isPending: createCustomer.isPending,
    isError: createCustomer.isError,
    error: createCustomer.error,
  };
};
